/**
 * Tenant visual expectation — when true, platform/cross-tenant defaults must not
 * render while authoritative branding/theme is unresolved.
 *
 * Does NOT carry asset URLs; only gates loading vs fallback presentation.
 */

const INVALID_TENANT_IDS = new Set(['', 'unknown']);

/** Read active tenant scope from browser storage (same source as tutor/API headers). */
export function readBrowserTenantId(): string | null {
  try {
    const raw = String(localStorage.getItem('X_TENANT_ID') || '').trim();
    if (!raw || INVALID_TENANT_IDS.has(raw)) return null;
    return raw;
  } catch {
    return null;
  }
}

/** Resolve tenant id from auth membership when storage is empty. */
export function resolveCurrentTenantId(options?: {
  user?: { memberships?: Array<{ school_id?: string | null }> } | null;
}): string | null {
  const fromStorage = readBrowserTenantId();
  if (fromStorage) return fromStorage;

  const memberships = options?.user?.memberships;
  if (!memberships?.length) return null;

  for (const membership of memberships) {
    const schoolId = String(membership?.school_id || '').trim();
    if (schoolId && !INVALID_TENANT_IDS.has(schoolId)) {
      return schoolId;
    }
  }
  return null;
}

/** True when the app has a concrete tenant scope (school/site), not generic platform. */
export function isTenantVisualsExpected(tenantId: string | null | undefined): boolean {
  const tid = String(tenantId || '').trim();
  return tid.length > 0 && !INVALID_TENANT_IDS.has(tid);
}
